import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchiseManager = ({ password }) => {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFranchises = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/franchises`);
      setFranchises(response.data);
    } catch (error) {
      toast.error('Failed to load franchises');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCache = async () => {
    try {
      const response = await axios.post(`${API}/admin/refresh-cache`, {}, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success(`Cache refreshed! ${response.data.franchises_count} franchises loaded`);
      fetchFranchises();
    } catch (error) {
      toast.error('Failed to refresh cache');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFranchises();
  }, []);

  if (loading) return <div style={styles.loading}>Loading franchises...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Franchise Management</h2>
        <button
          data-testid="refresh-franchises-btn"
          onClick={refreshCache}
          style={styles.refreshButton}
        >
          <RefreshCw size={18} />
          Refresh from Sheet
        </button>
      </div>

      <div style={styles.infoBox}>
        <Building2 size={20} color="#6b4423" />
        <div>
          <p style={styles.infoText}>
            Franchises are managed in your Google Sheet. To add, edit, or remove franchises:
          </p>
          <ol style={styles.infoList}>
            <li>Open your "1946 all Franchise Details" Google Sheet</li>
            <li>Make changes in the Sheet1 tab</li>
            <li>Click "Refresh from Sheet" button above to sync</li>
          </ol>
          <p style={styles.infoNote}>
            <strong>PIN Logic:</strong> Last 5 digits of Franchise Code
          </p>
        </div>
      </div>

      <div style={styles.statsCard}>
        <h3 style={styles.statsTitle}>Total Franchises</h3>
        <p style={styles.statsNumber}>{franchises.length}</p>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Franchise Code</th>
              <th style={styles.th}>Franchise Name</th>
              <th style={styles.th}>Location</th>
              <th style={styles.th}>Contact</th>
              <th style={styles.th}>PIN (Auto)</th>
            </tr>
          </thead>
          <tbody>
            {franchises.map((franchise, idx) => (
              <tr key={idx} style={styles.tr} data-testid={`franchise-row-${idx}`}>
                <td style={styles.td}>
                  <span style={styles.code}>{franchise.id}</span>
                </td>
                <td style={styles.td}>{franchise.name}</td>
                <td style={styles.td}>{franchise.location}</td>
                <td style={styles.td}>{franchise.contact}</td>
                <td style={styles.td}>
                  <span style={styles.pin}>
                    {franchise.id.slice(-5)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {franchises.length === 0 && (
          <div style={styles.emptyState}>
            <p>No franchises found. Please check your Google Sheet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    color: '#6b4423',
    margin: 0,
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#8b5e34',
  },
  infoBox: {
    background: '#fff8e8',
    border: '1px solid #e8dcc8',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
  },
  infoText: {
    margin: '0 0 12px',
    color: '#6b4423',
    fontSize: '14px',
  },
  infoList: {
    margin: '0 0 12px',
    paddingLeft: '20px',
    color: '#6b5d54',
    fontSize: '14px',
    lineHeight: '1.6',
  },
  infoNote: {
    margin: 0,
    color: '#6b4423',
    fontSize: '13px',
  },
  statsCard: {
    background: 'linear-gradient(135deg, #6b4423 0%, #8b5e34 100%)',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    color: 'white',
    marginBottom: '24px',
  },
  statsTitle: {
    fontSize: '16px',
    margin: '0 0 8px',
    opacity: 0.9,
  },
  statsNumber: {
    fontSize: '36px',
    fontWeight: '700',
    margin: 0,
  },
  tableWrapper: {
    overflowX: 'auto',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '16px',
    background: '#f9f6f1',
    color: '#6b4423',
    fontWeight: '600',
    borderBottom: '2px solid #e8dcc8',
    fontSize: '14px',
  },
  tr: {
    borderBottom: '1px solid #e8dcc8',
  },
  td: {
    padding: '12px 16px',
    color: '#4a3728',
    fontSize: '14px',
  },
  code: {
    background: '#f9f6f1',
    padding: '4px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '13px',
  },
  pin: {
    background: '#d1fae5',
    color: '#065f46',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: 'monospace',
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
    color: '#8b5e34',
  },
};

export default FranchiseManager;
